import type { HTMLAttributes, ReactNode } from 'react';

import { Badge } from '../components/badge';
import { cn } from '../lib/cn';

export type StatusBadgeStatus =
  | 'process-complete'
  | 'process-current'
  | 'process-future'
  | 'process-on-hold'
  | 'process-no-data'
  | 'service-open'
  | 'service-in-progress'
  | 'service-completed'
  | 'service-closed'
  | 'service-on-hold'
  | 'service-degraded'
  | 'confidence-high'
  | 'confidence-medium'
  | 'confidence-low'
  | 'trend-improving'
  | 'trend-stable'
  | 'trend-declining'
  // Story 2.2 (FR5/UX-DR1/AD-18) — dedicated Processen-coverage variants, distinct from the
  // generic `process-no-data` used elsewhere in the app: these map 1-op-1 to the shared
  // `CoverageState` enum (Story 2.1) so a coverage-consuming screen (2.3/2.4/3.3) never has to
  // invent its own color/icon heuristic. `coverage-no-data` and `coverage-publisher-offline`
  // are BOTH the informational, never-amber/red "b-nodata" surface (SM-C1: a blind spot must
  // never read as a bottleneck) — they are told apart by icon + label, not by color, so the
  // distinction survives with color disabled. `coverage-unmapped` is prepared here for Story
  // 2.4 (ongemapte procesactiviteit) on the separate "b-info" surface.
  | 'coverage-no-data'
  | 'coverage-publisher-offline'
  | 'coverage-unmapped';

export interface StatusBadgeProps extends Omit<HTMLAttributes<HTMLSpanElement>, 'aria-label' | 'children' | 'role'> {
  label: string;
  status: StatusBadgeStatus;
  tooltip?: string;
  'aria-label'?: string;
  role?: string;
  /**
   * Of de badge zichzelf als live region aankondigt (`role="status"`, de standaard). Uitzetten waar
   * veel badges tegelijk in beeld staan — zoals de tegels van een status-strip — anders praat elke
   * tegel bij elke ververs mee. Label, icoon en kleur blijven; alleen het aankondigen vervalt.
   */
  announce?: boolean;
}

interface StatusAppearance {
  className: string;
  icon: ReactNode;
}

function StatusIcon({ kind }: { kind: 'check' | 'current' | 'future' | 'pause' | 'none' | 'open' | 'closed' | 'degraded' | 'question' | 'edit' | 'trend-up' | 'trend-flat' | 'trend-down' | 'cloud-off' | 'help-center' }) {
  const paths = {
    check: <path d="m5 12 4 4L19 6" />,
    current: <><circle cx="12" cy="12" r="7" /><circle cx="12" cy="12" r="2" /></>,
    future: <circle cx="12" cy="12" r="7" />,
    pause: <><path d="M9 8v8M15 8v8" /></>,
    none: <><path d="M4 4l16 16" /><path d="M9.9 4.2A10.8 10.8 0 0 1 12 4c5 0 8 5 8 8a9.8 9.8 0 0 1-1.2 3.8M6.2 6.2C4.8 7.6 4 9.7 4 12c0 3 3 8 8 8 1 0 2-.2 2.8-.6" /></>,
    open: <path d="M5 12h14M12 5l7 7-7 7" />,
    closed: <><path d="M6 6l12 12M18 6 6 18" /></>,
    degraded: <><path d="M12 3 2 20h20L12 3Z" /><path d="M12 9v4m0 4h.01" /></>,
    question: <><path d="M9.1 9a3 3 0 1 1 5.8 1c-.7 1.1-1.9 1.4-2.5 2.4-.3.5-.4.9-.4 1.6" /><path d="M12 17h.01" /></>,
    edit: <><path d="m4 16 0 4h4L18.5 9.5l-4-4L4 16Z" /><path d="m12.5 7.5 4 4" /></>,
    'trend-up': <><path d="M4 16 10 10l4 4 6-6" /><path d="M14 6h6v6" /></>,
    'trend-flat': <path d="M4 12h16" />,
    'trend-down': <><path d="M4 8 10 14l4-4 6 6" /><path d="M14 18h6v-6" /></>,
    // Material Symbols "cloud_off" approximation — a cloud shape crossed out (publisher niet live, Story 2.2/DESIGN.md).
    'cloud-off': <><path d="M9.5 6.5A5 5 0 0 1 19 8a4 4 0 0 1-.6 7.95" /><path d="M6.3 8.13A4 4 0 0 0 7 16h9.5" /><path d="M3 3l18 18" /></>,
    // Material Symbols "help_center" approximation — a question mark inside a rounded square (ongemapte activiteit, Story 2.4-prep).
    'help-center': <><rect height="16" rx="4" width="16" x="4" y="4" /><path d="M9.5 9.8a2.5 2.5 0 1 1 4 1.9c-.7.5-1.5.9-1.5 2" /><path d="M12 17h.01" /></>,
  } satisfies Record<string, ReactNode>;

  return <svg aria-hidden="true" className="size-3.5 shrink-0" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24">{paths[kind]}</svg>;
}

const appearances: Record<StatusBadgeStatus, StatusAppearance> = {
  'process-complete': { className: 'bg-[var(--success-light)] text-[var(--foreground)]', icon: <StatusIcon kind="check" /> },
  'process-current': { className: 'bg-[var(--secondary-light)] text-[var(--foreground)]', icon: <StatusIcon kind="current" /> },
  'process-future': { className: 'border border-[var(--border)] bg-[var(--background-muted)] text-[var(--foreground-muted)]', icon: <StatusIcon kind="future" /> },
  'process-on-hold': { className: 'bg-[var(--warning-light)] text-[var(--warning-text)]', icon: <StatusIcon kind="pause" /> },
  'process-no-data': { className: 'border border-dashed border-[var(--border)] bg-[var(--background-muted)] text-[var(--foreground-muted)]', icon: <StatusIcon kind="none" /> },
  'service-open': { className: 'bg-[var(--secondary-light)] text-[var(--foreground)]', icon: <StatusIcon kind="open" /> },
  'service-in-progress': { className: 'bg-[var(--secondary-light)] text-[var(--foreground)]', icon: <StatusIcon kind="current" /> },
  'service-completed': { className: 'bg-[var(--success-light)] text-[var(--foreground)]', icon: <StatusIcon kind="check" /> },
  'service-closed': { className: 'bg-[var(--background-muted)] text-[var(--foreground-muted)]', icon: <StatusIcon kind="closed" /> },
  'service-on-hold': { className: 'bg-[var(--warning-light)] text-[var(--warning-text)]', icon: <StatusIcon kind="pause" /> },
  'service-degraded': { className: 'bg-[var(--info-light)] text-[var(--info-text)]', icon: <StatusIcon kind="degraded" /> },
  'confidence-high': { className: 'bg-[var(--success-light)] text-[var(--foreground)]', icon: <StatusIcon kind="check" /> },
  'confidence-medium': { className: 'bg-[var(--warning-light)] text-[var(--warning-text)]', icon: <StatusIcon kind="question" /> },
  'confidence-low': { className: 'bg-[var(--background-muted)] text-[var(--foreground-muted)]', icon: <StatusIcon kind="edit" /> },
  // h4-2/FR23: a declining trend is informational, never an error — amber, not red (DESIGN.md).
  'trend-improving': { className: 'bg-[var(--success-light)] text-[var(--foreground)]', icon: <StatusIcon kind="trend-up" /> },
  'trend-stable': { className: 'bg-[var(--background-muted)] text-[var(--foreground-muted)]', icon: <StatusIcon kind="trend-flat" /> },
  'trend-declining': { className: 'bg-[var(--warning-light)] text-[var(--warning-text)]', icon: <StatusIcon kind="trend-down" /> },
  // Story 2.2/SM-C1: both dekkingsgat-varianten share the gestreept/muted "b-nodata" surface —
  // NEVER amber/rood (that surface is reserved for a real knelpunt, Story 3.3) — and are told
  // apart from each other, and from a knelpunt, by icon + label (kleur-agnostisch leesbaar).
  'coverage-no-data': { className: 'border border-dashed border-[var(--border)] bg-[var(--background-muted)] text-[var(--foreground-muted)]', icon: <StatusIcon kind="question" /> },
  'coverage-publisher-offline': { className: 'border border-dashed border-[var(--border)] bg-[var(--background-muted)] text-[var(--foreground-muted)]', icon: <StatusIcon kind="cloud-off" /> },
  // Story 2.4-prep: ongemapte activiteit is its own "b-info" surface (blue), distinct from the
  // dashed no-data surface — an unmapped event is a KNOWN raw code, not an absence of data.
  'coverage-unmapped': { className: 'bg-[var(--info-light)] text-[var(--info-text)]', icon: <StatusIcon kind="help-center" /> },
};

export function StatusBadge({ announce = true, className, label, status, tooltip, ...props }: StatusBadgeProps) {
  const { icon: _ignoredIcon, ...safeProps } = props as typeof props & { icon?: unknown };
  const appearance = appearances[status];

  return (
    <Badge {...safeProps} aria-label={label} className={cn('gap-1 text-[11px]', appearance.className, className)} role={announce ? 'status' : undefined} title={tooltip}>
      <span aria-label={label} role="img">{appearance.icon}</span>
      <span>{label}</span>
    </Badge>
  );
}
